import { useEffect, useMemo } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

import { APPLICATION_ROUTES } from 'constants/routes';

// material-ui
import { styled, useTheme, Theme } from '@mui/material/styles';
import { AppBar, Box, Container, CssBaseline, Toolbar, useMediaQuery } from '@mui/material';

// project imports
import Header from './Header';
import Sidebar from './Sidebar';
import HorizontalBar from './HorizontalBar';
import Breadcrumbs from 'shared/components/ui-component/extended/Breadcrumbs';

import navigation from 'shared/components/menu-items';
import LAYOUT_CONST, { MAIN_MARGIN_TOP_PX, MAIN_OFFSET_HORIZONTAL_LAYOUT_PX } from 'constant';
import useConfig from 'shared/hooks/useConfig';
import { drawerWidth } from 'shared/store/constant';
import { openDrawer } from 'shared/store/slices/menu';
import { useDispatch, useSelector } from 'shared/store';

// assets
import { IconChevronRight } from '@tabler/icons';

interface MainStyleProps {
    theme: Theme;
    open: boolean;
    layout: string;
}

// styles
const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })(({ theme, open, layout }: MainStyleProps) => ({
    ...theme.typography.mainContent,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    ...(!open && {
        transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.shorter + 200
        }),
        [theme.breakpoints.up('md')]: {
            marginLeft: layout === LAYOUT_CONST.VERTICAL_LAYOUT ? -(drawerWidth - 72) : '20px',
            width: `calc(100% - ${drawerWidth}px)`,
            marginTop: layout === LAYOUT_CONST.HORIZONTAL_LAYOUT ? MAIN_OFFSET_HORIZONTAL_LAYOUT_PX : MAIN_MARGIN_TOP_PX
        },
        // Below md the sidebar is a temporary overlay, so content takes full width
        [theme.breakpoints.down('md')]: {
            marginLeft: 0,
            width: '100%',
            padding: '8px 16px 16px',
            marginTop: MAIN_MARGIN_TOP_PX
        }
    }),
    ...(open && {
        // 'margin 538ms cubic-bezier(0.4, 0, 1, 1) 0ms',
        transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.shorter + 200
        }),
        marginLeft: layout === LAYOUT_CONST.HORIZONTAL_LAYOUT ? '20px' : 0,
        marginTop: layout === LAYOUT_CONST.HORIZONTAL_LAYOUT ? MAIN_OFFSET_HORIZONTAL_LAYOUT_PX : MAIN_MARGIN_TOP_PX,
        width: `calc(100% - ${drawerWidth}px)`,
        [theme.breakpoints.up('md')]: {
            marginTop: layout === LAYOUT_CONST.HORIZONTAL_LAYOUT ? MAIN_OFFSET_HORIZONTAL_LAYOUT_PX : MAIN_MARGIN_TOP_PX
        },
        // Below md the sidebar is a temporary overlay, so content takes full width
        [theme.breakpoints.down('md')]: {
            marginLeft: 0,
            width: '100%',
            padding: '8px 16px 16px',
            marginTop: MAIN_MARGIN_TOP_PX
        }
    })
}));

// ==============================|| MAIN LAYOUT ||============================== //

const MainLayout = () => {
    const theme = useTheme();
    const { pathname } = useLocation();
    const showBreadcrumbs = pathname !== APPLICATION_ROUTES.dashboard;

    const matchDownMd = useMediaQuery(theme.breakpoints.down('md'));

    const dispatch = useDispatch();
    const { drawerOpen } = useSelector((state) => state.menu);
    const { drawerType, container, layout } = useConfig();

    useEffect(() => {
        if (drawerType === LAYOUT_CONST.DEFAULT_DRAWER) {
            dispatch(openDrawer(true));
        } else {
            dispatch(openDrawer(false));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [drawerType]);

    useEffect(() => {
        if (drawerType === LAYOUT_CONST.DEFAULT_DRAWER) {
            dispatch(openDrawer(true));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (matchDownMd) {
            dispatch(openDrawer(true));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [matchDownMd]);

    const condition = layout === LAYOUT_CONST.HORIZONTAL_LAYOUT && !matchDownMd;

    const header = useMemo(
        () => (
            <Toolbar
                sx={{
                    minHeight: '62px !important',
                    px: condition ? 1.25 : 2,
                    py: 0.75
                }}
            >
                <Header />
            </Toolbar>
        ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [layout, matchDownMd]
    );

    return (
        <Box sx={{ display: 'flex' }}>
            <CssBaseline />
            {/* header */}
            <AppBar
                enableColorOnDark
                position="fixed"
                color="inherit"
                elevation={0}
                sx={{
                    bgcolor: theme.palette.background.default,
                    // App.jsx — header border under top bar (T.borderLight)
                    ...(theme.palette.mode === 'light' && {
                        borderBottom: '1px solid #F0EDEA',
                        backgroundImage: 'none'
                    })
                }}
            >
                {header}
            </AppBar>

            {/* horizontal menu-list bar */}
            {layout === LAYOUT_CONST.HORIZONTAL_LAYOUT && !matchDownMd && <HorizontalBar />}

            {/* drawer */}
            {(layout === LAYOUT_CONST.VERTICAL_LAYOUT || matchDownMd) && <Sidebar />}

            {/* main content */}
            <Main theme={theme} open={drawerOpen} layout={layout}>
                <Container maxWidth={container ? 'lg' : false} disableGutters={!container}>
                    {showBreadcrumbs && (
                        <Breadcrumbs card={false} separator={IconChevronRight} navigation={navigation} icon title rightAlign />
                    )}
                    <Outlet />
                </Container>
            </Main>
        </Box>
    );
};

export default MainLayout;
